import "./MyBookings.scss";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { useEffect, useState, useMemo, useContext, useCallback } from "react";
// import Pagination from "../../../components/Pagination/Pagination";
import { AuthContext } from "../../../contexts/AuthContext";
import Table from "../../../components/Table/Table";
import { DynamicSelect } from "../../../components/Inputs/Inputs";
import AlertModal from "../../../components/AlertModal/AlertModal";
import DisplayInfo from "../../../components/DisplayInfo/DisplayInfo";
import Pagination from "../../../components/Pagination/Pagination";

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const diffNights = (checkin, checkout) => {
  if (!checkin || !checkout) return 0;
  // datas ISO "YYYY-MM-DD" são interpretadas como UTC mas é ok para diff.
  const ci = new Date(checkin);
  const co = new Date(checkout);
  const ms = co - ci;
  if (Number.isNaN(ms)) return 0;
  const nights = Math.round(ms / (1000 * 60 * 60 * 24));
  return Math.max(0, nights);
};

export default function MyBookings() {
  const {
    axiosAuth,
    currentUser,
    accessibleHotels = [],
    accessibleGroups = [],
  } = useContext(AuthContext);
  const isGroupAdmin = currentUser?.role === "group_admin";
  const groupId = useMemo(
    () =>
      isGroupAdmin || accessibleGroups[0]?.id ? accessibleGroups[0].id : null,
    [isGroupAdmin, accessibleGroups]
  );

  // opções de hotel com base no user logado
  const hotelOptions = useMemo(() => {
    const base = (accessibleHotels || []).map((h) => ({
      value: String(h.id),
      label: h.name,
    }));
    return isGroupAdmin && base.length > 0
      ? [{ value: "ALL", label: "Todos os hotéis" }, ...base]
      : base;
  }, [accessibleHotels, isGroupAdmin]);

  // hotel selecionado (default: all para group_admin; primeiro hotel para hotel_admin)
  const [selectedHotelId, setSelectedHotelId] = useState("");
  useEffect(() => {
    if (!selectedHotelId) {
      if (isGroupAdmin && hotelOptions.length > 0) {
        setSelectedHotelId(hotelOptions[0].value); // all
      } else if (!isGroupAdmin && hotelOptions.length > 0) {
        setSelectedHotelId(hotelOptions[0].value);
      }
    } else {
      // se o hotel selecionado deixar de existir nas opções, ajusta
      if (!hotelOptions.some((o) => o.value === selectedHotelId)) {
        setSelectedHotelId(hotelOptions[0]?.value || "");
      }
    }
  }, [hotelOptions, selectedHotelId, isGroupAdmin]);

  // dados
  const [bookingEvents, setBookingEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // paginação
  const [page, setPage] = useState(1);
  const limit = 4;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.ceil(total / limit), [total]);

  // erros
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: "", message: "" }),
    []
  );

  // retornar os eventos
  const fetchBookingEvents = useCallback(async () => {
    if (!selectedHotelId) {
      setBookingEvents([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const params =
        selectedHotelId === "ALL"
          ? {
              group_id: Number(groupId),
              skip: (page - 1) * limit,
              limit,
            }
          : {
              group_id: Number(groupId),
              hotel_id: Number(selectedHotelId),
              skip: (page - 1) * limit,
              limit,
            };

      const { data } = await axiosAuth.get("/form-events/", { params });

      const results = Array.isArray(data?.results) ? data.results : [];
      const totalFromApi =
        typeof data?.total === "number" ? data.total : results.length;

      setBookingEvents(results);
      setTotal(totalFromApi);
    } catch (err) {
      openError(
        "Erro ao carregar reservas",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedHotelId, groupId, openError, page, limit]);

  useEffect(() => {
    fetchBookingEvents();
  }, [fetchBookingEvents]);

  // tabela
  const columns = useMemo(
    () => [
      { key: "hotel_name", label: "Hotel" },
      { key: "checkin_date", label: "Check-in" },
      { key: "checkout_date", label: "Check-out" },
      { key: "nights", label: "Noites" },
      { key: "adults", label: "Adultos" },
      { key: "children", label: "Crianças" },
      { key: "infants", label: "Bebés" },
      { key: "promocode", label: "Promocode" },
    ],
    []
  );

  const tableData = useMemo(
    () =>
      bookingEvents.map((b) => ({
        id: b.id,
        hotel_name: b.hotel_name,
        checkin_date: formatDate(b.checkin_date),
        checkout_date: formatDate(b.checkout_date),
        nights: diffNights(b.checkin_date, b.checkout_date),
        adults: b.adults,
        children: b.children,
        infants: b.infants,
        promocode: b.promocode || "",
      })),
    [bookingEvents]
  );

  console.log("groupdId:", groupId);
  console.log("selectedHotelId:", selectedHotelId);

  return (
    <div className="booking-reservations-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <PageTitle title="Reservas" />

      <div className="filters">
        <DynamicSelect
          label="Hotel"
          placeholder="Escolher um hotel"
          value={selectedHotelId}
          onChange={(e) => setSelectedHotelId(e.target.value)}
          options={hotelOptions}
          disabled={loading || hotelOptions.length === 0}
        />
      </div>

      {total > limit && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      )}

      <div className="container">
        {!loading && selectedHotelId && tableData.length === 0 && (
          <DisplayInfo variant="info" message="Sem reservas para este hotel" />
        )}
        {tableData.length !== 0 && <Table columns={columns} data={tableData} />}
      </div>
    </div>
  );
}
